<template>
  <div>
    <div v-if="spinners.contentIsLoading" class="text-center mt-5 mb-5">
      <b-spinner type="grow" variant="primary"/>
    </div>
    <b-row v-else class="card-body">
      <b-col cols="6" xl="4">
        <b-form-group :label="'supplier' | translate('global')">
          <treeselect value-format="object" :default-expand-level="1"
                      :placeholder="'supplier_select' | translate('global')"
                      instanceId="loadSuppliers" :class="{ 'treeselect-error' : errorMsg.supplier_id }"
                      :load-options="loadOptions" :async="true" :cacheOptions="false"
                      :searchPromptText="'supplier_autocomplete' | translate('global')"
                      :noResultsText="'no_results_found' | translate('global')"
                      v-model="selectedSupplier"
                      :disabled="!actions.update"/>
          <span class="error-text" v-if="errorMsg.supplier_id">{{ errorMsg.supplier_id[0] }}</span>
        </b-form-group>
      </b-col>
      <b-col cols="6" xl="4">
        <b-form-group :label="'structure' | translate('global')">
          <treeselect :class="{ 'treeselect-error' : errorMsg.structures }"
                      :multiple="true"
                      :placeholder=" 'select_structure' | translate('global') "
                      :options="structureOptions" 
                      v-model="invoice.structures"
                      :disabled="!actions.update"/>
          <span class="error-text" v-if="errorMsg.structures">{{ errorMsg.structures[0] }}</span>
        </b-form-group>
      </b-col>
      <b-col cols="6" xl="4">
        <b-form-group :label="'invoice_number' | translate('global')">
          <b-form-input :placeholder="'invoice_number_placeholder' | translate('global')"
                        :class="{ 'is-invalid' : errorMsg.number }"
                        v-model="invoice.number"
                        :disabled="!actions.update"/>
          <span class="error-text" v-if="errorMsg.number">{{ errorMsg.number[0] }}</span>
        </b-form-group>
      </b-col>
      <b-col cols="6" xl="4">
        <b-form-group :label="'invoice_issue_at' | translate('global')">
          <datepicker :format="'DD.MM.YYYY'" :value-type="'format'"
                      :input-class="'datepicker-input form-control'"
                      :class="{ 'date-picker-error': errorMsg.issue_at }"
                      :placeholder="'invoice_issue_at_placeholder' | translate('global')"
                      v-model="invoice.issue_at"
                      :disabled="!actions.update"/>
          <span class="error-text" v-if="errorMsg.issue_at">{{errorMsg.issue_at[0]}}</span>
        </b-form-group>
      </b-col>
      <b-col cols="6" xl="4">
        <b-form-group :label="'invoice_payment_term' | translate('global')">
          <treeselect value-format="object"
                      :class="{ 'treeselect-error' : errorMsg.payment_terms_classifier_id }"
                      :placeholder=" 'invoice_payment_term_placeholder' | translate('global') "
                      :options="paymentTermsOptions" v-model="selectedPaymentTerm"
                      :disabled="!actions.update"/>
          <span class="error-text" v-if="errorMsg.payment_terms_classifier_id">
                      {{ errorMsg.payment_terms_classifier_id[0] }}
                    </span>
        </b-form-group>
      </b-col>
      <b-col cols="6" xl="4">
        <b-form-group :label="'sum' | translate('global')">
          <b-form-input type="number" min="0"  :placeholder=" 'add_sum' | translate('global')"
                        :class="{ 'is-invalid' : errorMsg.sum }"
                        v-model="invoice.sum"
                        :disabled="!actions.update"/>
          <span class="error-text" v-if="errorMsg.sum">{{ errorMsg.sum[0] }}</span>
        </b-form-group>
      </b-col>
      <b-col cols="12">
        <b-form-group :label="'invoice_description' | translate('global')">
          <b-form-textarea :placeholder=" 'add_comment' | translate('global') " rows="3" max-rows="6"
                           :class="{ 'is-invalid' : errorMsg.comment }" 
                           v-model="invoice.comment"
                           :disabled="!actions.update"/>
          <div class="invalid-feedback" v-if="errorMsg.comment">{{ errorMsg.comment[0] }}</div>
        </b-form-group>
      </b-col>
      <b-col cols="12">
        <b-form-group :label="'attachments_comment' | translate('global')">
          <b-form-textarea rows="3" max-rows="6"
                           :placeholder=" 'add_attachments_comment' | translate('global') "
                           :class="{ 'is-invalid' : errorMsg.attachments_comment }"
                           :disabled="!actions.update"
                           v-model="invoice.attachments_comment"/>
          <div class="invalid-feedback" v-if="errorMsg.attachments_comment">
            {{ errorMsg.attachments_comment[0] }}
          </div>
        </b-form-group>
      </b-col>
      <b-col cols="12" class="text-right">
        <b-button type="submit" variant="success" class="mt-3 mr-2" :disabled="spinners.isSaving || !actions.update"
                  @click="onSave()">
          <b-spinner v-if="spinners.isSaving" type="grow" small/>
          {{ 'save' | translate('global') }}
        </b-button>
        <Delete/>
      </b-col>
      <b-col cols="12">
        <Attachments :isNew="false"/>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import statuses from '@/store/statuses';
import forEach from 'lodash.foreach';
import VuexTypes from '@/store/types/index';
import Services from '@/services/index';
import TreeselectMixin from '@/mixins/TreeselectMixin';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import Datepicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/lv';
import Attachments from '../Attachments/Attachments.vue';
import Delete from './InvoiceDelete.vue';

const InvoiceTypes = VuexTypes.invoices;
const SuppliersService = Services.suppliers;

export default {
  mounted() {},
  mixins: [TreeselectMixin],
  data() {
    return {
      spinners: {
        contentIsLoading: false,
        isSaving: false,
      },
      invoice: {
        supplier_id: null,
        number: null,
        issue_at: null,
        sum: '',
        comment: '',
        attachments_comment: '',
        structures: [],
      },
      selectedSupplier: null,
      selectedPaymentTerm: null,
      paymentTermsOptions: [],
      structureOptions: [],
      errorMsg: {},
      actions: {}
    };
  },
  components: { Treeselect, Datepicker, Attachments, Delete },
  computed: {
    ...mapGetters('Invoices', [
      'invoiceUpdateData',
      'invoiceUpdate',
      'invoiceClassifiers',
    ]),
  },
  watch: {
    invoiceUpdateData: {
      immediate: true,
      handler: function () {
        if (this.invoiceUpdateData.status === statuses.LOADED) {
          this.spinners.contentIsLoading = false;
          this.fillData();
        } else if (this.invoiceUpdateData.status === statuses.LOADING
          || this.invoiceUpdateData.status === statuses.EMPTY) {
          this.spinners.contentIsLoading = true;
        }
      },
      deep: true
    },
    invoiceClassifiers: {
      immediate: true,
      handler: function () {
        if (this.invoiceClassifiers.status === statuses.LOADED) {
          this.fillClassifiers();
        }
      },
      deep: true
    },
    invoiceUpdate: {
      handler: function () {
        this.errorMsg = {};
        this.spinners.isSaving = false;
        if (this.invoiceUpdate.status === statuses.LOADED) {
          this.alertSuccess();
          this.getData();
        } else if (this.invoiceUpdate.status === statuses.ERROR) {
          this.alertError();
          this.errorMsg = this.invoiceUpdate.data.data.errors;
        }
      },
      deep: true
    },
    selectedSupplier: function () {
      this.invoice['supplier_id'] = this.selectedSupplier ? this.selectedSupplier['id'] : null;
      const invoice = this.invoiceUpdateData.data.invoice ? this.invoiceUpdateData.data.invoice : null;

      if (this.selectedSupplier && invoice && this.selectedSupplier['id'] !== invoice['supplier']['id']) {
        this.selectedPaymentTerm = this.selectedSupplier['paymentTermsClassifier'] ? {
          id: this.selectedSupplier['paymentTermsClassifier']['id'],
          label: this.selectedSupplier['paymentTermsClassifier']['name']
        } : null;
      }
    },
    selectedPaymentTerm: function () {
      this.invoice['payment_terms_classifier_id'] = this.selectedPaymentTerm
        ? this.selectedPaymentTerm['id'] : null;
    },
  },
  methods: {
    ...mapActions('Invoices', [
      InvoiceTypes.INVOICE_UPDATE_DATA,
      InvoiceTypes.INVOICE_UPDATE
    ]),
    getData() {
      this.INVOICE_UPDATE_DATA(this.$route.params.id);
    },
    fillData() {
      const invoice = this.invoiceUpdateData.data.invoice;
      this.actions = invoice['actions'] ? invoice['actions'] : {};
      this.invoice['number'] = invoice['number'];
      this.invoice['issue_at'] = invoice['issue_at'];
      this.invoice['sum'] = invoice['sum'];
      this.invoice['comment'] = invoice['comment'];
      this.invoice['attachments_comment'] = invoice['attachments_comment'];
      this.invoice['supplier_id'] = invoice.supplier ? invoice['supplier']['id'] : null;
      this.invoice['payment_terms_classifier_id'] = invoice.paymentTermsClassifier
        ? invoice['paymentTermsClassifier']['id'] : null;

      this.selectedSupplier = invoice['supplier'] ? {
        id: invoice['supplier']['id'],
        label: invoice['supplier']['name']
      } : null;

      this.selectedPaymentTerm = invoice['paymentTermsClassifier'] ? {
        id: invoice['paymentTermsClassifier']['id'],
        label: invoice['paymentTermsClassifier']['name']
      } : null;

      const structures = [];
      if (invoice['structures'].length) {
        forEach(invoice['structures'], (structure) => {
          structures.push(structure['id']);
        });
      }
      this.invoice.structures = structures;
    },

    fillClassifiers() {
      this.structureOptions = this.fillTreeselectDropdown(this.invoiceClassifiers.data['structures'], 'id', 'name');
      this.paymentTermsOptions = this.fillTreeselectDropdown(
        this.invoiceClassifiers.data['paymentTermsClassifier'], 'id', 'name'
      );
    },
    async loadOptions({ action, searchQuery, callback, instanceId }) {
      let options = [];
      const query = searchQuery.trim();
      if (action === 'ASYNC_SEARCH') {
        const valid = this.checkSearchQuery(query);
        if (valid) {
          options = await this[instanceId](query);
        }
      }
      callback(null, options);
    },
    async loadSuppliers(name) {
      const param = name || '';
      const data = await SuppliersService.search(param);
      const options = [];
      forEach(data.data.data, (item) => {
        options.push({
          id: item['id'],
          label: item['name'],
          paymentTermsClassifier: item['payment_terms_classifier'] ? {
            id: item['payment_terms_classifier']['id'],
            name: item['payment_terms_classifier']['name']
          } : null
        });
      });
      return options;
    },
    onSave() {
      this.spinners.isSaving = true;
      this.INVOICE_UPDATE({ id: this.$route.params.id, update: this.invoice });
    }
  }
};
</script>

<style scoped>
</style>
